import immer from 'immer';
import { pick, merge, get } from 'lodash';
import * as Sentry from '@sentry/browser';
import { isClient, getQueryParams } from 'app/lib/utils';
import { getUtcTimestamp } from 'app/lib/time';
import { cookies, sessionStorage, localStorage } from 'app/lib/storage';
import * as Analytics from 'app/lib/analytics';
import Tenancy from 'app/lib/tenancy';
import heydoctor from 'app/vendor/heydoctor';
import { AddressData, User, SignIn, LegalDocument } from '@goodrx/heydoctor-sdk';
import { NotificationChannels, NotificationStatuses } from 'app/config/constants';
import { LegalCodes } from '@goodrx/heydoctor-core';
import { Stores, Store } from 'app/lib/synaptik';

const initialOnboardingState = {
  cache: {},
  profile: {},
  errors: {},
};

const initialState: State = {
  profile: null,
  token: null,
  signingIn: false,
  signingUp: false,
  reauthenticating: false,
  authenticated: false,
  subscription: null,
  paymentMethods: [],
  legalsRequiringAgreement: [],
  showPhoneVerificationModal: false,
  topicToVerify: undefined,
  onboarding: { ...initialOnboardingState },
  hideEmailBanner: false,
};

const TOKEN = 'hd.auth_token';

export const REQUIRED_ONBOARDING_FIELDS = [
  'firstName',
  'lastName',
  'dob',
  'gender',
  'phone',
  'addressLine1',
  'city',
  'state',
  'zip',
];

export const REQUIRED_PROFILE_FIELDS = ['email', ...REQUIRED_ONBOARDING_FIELDS];
export const REQUIRED_ONBOARDING_FIELDS_WITHOUT_ADDRESS = ['firstName', 'lastName', 'dob', 'gender', 'phone'];
export const UPDATABLE_PROFILE_FIELDS = ['addressLine2', 'currentState', ...REQUIRED_PROFILE_FIELDS];

interface State {
  profile: (User & AddressData & { legalDocuments: LegalDocument[]; dob: string; gender: string }) | null;
  token: any;
  authenticated: boolean;
  subscription: any;
  paymentMethods: any;
  legalsRequiringAgreement: LegalDocument[];
  showPhoneVerificationModal: boolean;
  topicToVerify?: any;
  onboarding: any;
  hideEmailBanner: boolean;

  // ** Magic types that depends on runAsync key below
  reauthenticating?: boolean;
  signingUp?: boolean;
  signingIn?: boolean;
  sendingForgotPasswordEmail?: boolean;
  resettingPassword?: boolean;
  agreeingToLegals?: boolean;
  updatingProfile?: boolean;
  updatingNotification?: boolean;
  sendingPhoneVerification?: boolean;
  verifyingPhone?: boolean;
  fetchingPaymentMethods?: boolean;
  creatingPaymentMethod?: boolean;
  deletingPaymentMethod?: boolean;
  fetchingSubscription?: boolean;
  creatingSubscription?: boolean;
  cancellingSubscription?: boolean;
  verifyingEmail?: boolean;
  isSendingEmailVerification?: boolean;
  loadingPlan?: boolean;
  sendingPhoneVerificationError?: boolean;
  revokingLegals?: boolean;
}

export class UserStore extends Store<State, Stores> {
  state: State = { ...initialState };

  constructor(...args: [any, any]) {
    super(...args);

    heydoctor.client.on('unauthorized', this.logout);

    if (isClient()) {
      this.state.reauthenticating = true;
      this.reauthenticate();

      const onboarding = sessionStorage.get('onboarding');
      if (onboarding) {
        this.state.onboarding = merge({}, this.state.onboarding, onboarding);
      }
    }
  }

  reauthenticate = async () =>
    this.runAsync({
      key: 'reauthenticating',
      work: async () => {
        try {
          const accessToken = localStorage?.get(TOKEN);
          const { access_token: webviewAccessToken } = getQueryParams();
          if (accessToken || webviewAccessToken) {
            // ensures that new access tokens passed in URL overwrite previous one in localstorage
            if (webviewAccessToken) {
              heydoctor.client.setAccessToken(webviewAccessToken);
            } else if (accessToken) {
              heydoctor.client.setAccessToken(accessToken);
            }

            const user = await heydoctor.users.fetch();
            this.setAuthenticatedUser(user);
          }
        } catch {}
      },
    });

  setAuthenticatedUser = (user: any) => {
    this.stores.chat.subscribe();
    this.verifyLegalAgreements(user);

    const accessToken = heydoctor.client.getAccessToken();

    if (localStorage) {
      localStorage.set(TOKEN, accessToken);
    }

    Sentry.configureScope((scope) => {
      scope.setUser({ id: user.id });
    });

    this.setState({
      profile: user,
      authenticated: true,
    });
  };

  isOnboarded = (skipAddress?: boolean) => {
    if (!this.state.profile) return false;
    const requiredFields = skipAddress ? REQUIRED_ONBOARDING_FIELDS_WITHOUT_ADDRESS : REQUIRED_ONBOARDING_FIELDS;
    return requiredFields.every((field) => !!this.state.profile?.[field]);
  };

  signUp = async (profile: { email: string; password: string; consentedToHma: boolean }) =>
    this.runAsync({
      key: 'signingUp',
      work: async () => {
        const { partner = Tenancy.getTenant(), ...extraParams } = cookies.get()?.queryParams || {};
        const user = await heydoctor.users.signUp({
          ...profile,
          // @ts-ignore
          partner,
          referrer_params: extraParams as Record<string, string>,
          consented_to_hma: profile.consentedToHma,
        });

        Analytics.onUserSignUp({ id: user.id });

        this.setAuthenticatedUser(user);

        return user;
      },
    });

  registerEcommUser = async ({
    consentedToHma,
    currentState,
    serviceSlug,
    requestedDrug,
    ...profile
  }: {
    email: string;
    password: string;
    dob?: string;
    consentedToHma: boolean;
    currentState?: string;
    serviceSlug?: string;
    requestedDrug?: { displayName: string; dose: string; quantity: number; rxcui: string; cost: number };
  }) =>
    this.runAsync({
      key: 'signingUp',
      work: async () => {
        const { partner = Tenancy.getTenant(), ...extraParams } = cookies.get()?.queryParams || {};
        const { user, visit } = await heydoctor.users.registerEcommUser({
          ...profile,
          // @ts-ignore
          partner,
          referrer_params: extraParams as Record<string, string>,
          consented_to_hma: consentedToHma,
          service_slug: serviceSlug,
          current_state: currentState,
          requested_drug: pick(requestedDrug, ['displayName', 'dose', 'quantity', 'rxcui', 'cost']),
        });

        Analytics.onUserSignUp({ id: user.id });

        this.stores.visits.setActiveVisit(visit);

        this.setAuthenticatedUser(user);

        Analytics.onVisitStarted();

        return user;
      },
    });

  signIn = async ({ email, password }: SignIn) =>
    this.runAsync({
      key: 'signingIn',
      work: async () => {
        const user = await heydoctor.users.signIn({ email, password } as any);

        this.setAuthenticatedUser(user);

        Analytics.onUserSignIn({ id: user.id });

        return user;
      },
    });

  sendForgotPasswordEmail = async (email: any) =>
    this.runAsync({
      key: 'sendingForgotPasswordEmail',
      work: async () => heydoctor.users.sendForgotPasswordEmail(email),
    });

  resetPassword = async ({ email, token, password }: any) =>
    this.runAsync({
      key: 'resettingPassword',
      work: () => heydoctor.users.resetPassword(email, token, password),
    });

  verifyLegalAgreements = async (user: User) => {
    const userLegals = await user.getLegalAgreements();

    const legalsRequiringAgreement = userLegals.reduce((acc, doc) => {
      /**
       * If true, a new version of the legal document has been released
       */
      if (doc.version > doc.initialVersion && doc.code !== LegalCodes.HIPAAMarketingAuthorization) {
        return [...acc, doc];
      }
      return acc;
    }, []);

    if (legalsRequiringAgreement.length === 0) return;

    this.setState({
      legalsRequiringAgreement,
    });
  };

  agreeToLegals = (legalDocumentCodes: LegalCodes[] = []) =>
    this.runAsync({
      key: 'agreeingToLegals',
      work: async () => {
        const documentCodesToUpdate = legalDocumentCodes.length
          ? legalDocumentCodes
          : this.state.legalsRequiringAgreement.map((doc) => doc.code);

        // TODO(hma): convert to SDK
        const {
          data: { data },
        } = await heydoctor.client.http.post('/users/me/legals', {
          codes: documentCodesToUpdate,
        });

        this.setState(
          immer((draft) => {
            draft.legalsRequiringAgreement = [];
            draft.profile!.legalDocuments = data;
          })
        );
      },
    });

  revokeLegalAgreement = (legalDocumentCode: LegalCodes) =>
    this.runAsync({
      key: 'revokingLegals',
      work: async () => {
        // TODO(hma): convert to SDK
        await heydoctor.client.http.delete(`/users/me/legals/${legalDocumentCode}`);

        this.setState(
          immer((draft: State) => {
            draft.profile!.legalDocuments = draft.profile!.legalDocuments.filter(
              (doc) => doc.code !== legalDocumentCode
            );
          })
        );
      },
    });

  updateProfile = async (updates: any) =>
    this.runAsync({
      key: 'updatingProfile',
      work: async () => {
        const profile = await this.state.profile?.update(pick(updates, UPDATABLE_PROFILE_FIELDS) as any);

        this.setState({ profile });

        Analytics.onUserUpdated();
      },
    });

  updateNotificationTopic = async ({ channel, topic, status }: { channel: string; topic: string; status: string }) =>
    this.runAsync({
      key: 'updatingNotification',
      work: async () => {
        const notificationHandlers = {
          [NotificationChannels.SMS]: this.toggleSmsNotifications,
        };

        const handler = notificationHandlers[channel];

        if (handler) {
          const shouldToggle = await handler({ topic, status, channel });
          if (!shouldToggle) return;
        }

        const profile = await this.state.profile?.updateNotificationTopic({ topic, status, channel });

        this.setState(
          immer((state) => {
            // @ts-ignore
            state.profile = profile;
            state.topicToVerify = undefined;
          })
        );

        Analytics.onNotificationUpdated({ topic, channel, status });
      },
    });

  toggleSmsNotifications = async ({ channel, status, topic }: any) => {
    const phoneVerifiedAt = get(this.state, 'profile.phoneVerifiedAt');

    if (channel === NotificationChannels.SMS && status === NotificationStatuses.ENABLED && !phoneVerifiedAt) {
      this.sendPhoneVerification();
      this.setState({ topicToVerify: topic, showPhoneVerificationModal: true });
      return false;
    }

    return true;
  };

  setShowPhoneVerificationModal = (value: any) => {
    this.setState({
      showPhoneVerificationModal: value,
    });
  };

  sendPhoneVerification = () =>
    this.runAsync({
      key: 'sendingPhoneVerification',
      work: () => this.state.profile?.sendPhoneVerification(),
    });

  verifyPhone = async (code: any) =>
    this.runAsync({
      key: 'verifyingPhone',
      work: async () => {
        await this.state.profile?.verifyPhone(code);

        this.setState(
          immer((state) => {
            state.showPhoneVerificationModal = false;
            // @ts-ignore
            state.profile!.phoneVerifiedAt = getUtcTimestamp();
          })
        );
      },
    });

  logout = async () => {
    heydoctor.client.setAccessToken('');

    Sentry.configureScope((scope) => {
      scope.setUser({});
    });

    if (localStorage) {
      localStorage.remove(TOKEN);
    }
    this.stores.questionnaire.reset();
    this.stores.chat.unsubscribe();
    this.setState({ ...initialState });
  };

  getExistingVisitId = async (serviceCode: any) => {
    const existingVisits = await this.state.profile?.getExistingVisits(serviceCode);
    return existingVisits && existingVisits.length ? existingVisits[0].id : undefined;
  };

  isProfileComplete = () => {
    const { profile } = this.state;
    return REQUIRED_PROFILE_FIELDS.every((field) => !!profile?.[field]);
  };

  isOnboardingProfileComplete = (skipAddress?: boolean) => {
    const { onboarding } = this.state;
    const requiredFields = skipAddress ? REQUIRED_ONBOARDING_FIELDS_WITHOUT_ADDRESS : REQUIRED_ONBOARDING_FIELDS;
    return requiredFields.every((field) => !!onboarding.profile[field]);
  };

  /**
   * Payment Methods
   */
  fetchPaymentMethods = async () =>
    this.runAsync({
      key: 'fetchingPaymentMethods',
      work: async () => {
        const data = await this.state.profile?.fetchPaymentMethods();

        this.setState({
          paymentMethods: data,
        });

        return data;
      },
    });

  createPaymentMethod = (payload: any) =>
    this.runAsync({
      key: 'creatingPaymentMethod',
      work: async () => {
        const data = await this.state.profile?.savePaymentMethod(payload);

        this.setState({
          paymentMethods: [...this.state.paymentMethods, data],
        });
      },
    });

  deletePaymentMethod = (token: any) =>
    this.runAsync({
      key: 'deletingPaymentMethod',
      work: async () => {
        await this.state.profile?.deletePaymentMethod(token);
        this.setState({
          paymentMethods: this.state.paymentMethods.filter((pm: any) => pm.token !== token),
        });
      },
    });

  fetchSubscription = () =>
    this.runAsync({
      key: 'fetchingSubscription',
      work: async () => {
        const data = await this.state.profile?.fetchSubscription();

        this.setState({
          subscription: data,
        });
      },
    });

  createSubscription = (payload: any) =>
    this.runAsync({
      key: 'creatingSubscription',
      work: async () => {
        const data = await this.state.profile?.createSubscription(payload);

        this.setState({
          subscription: data,
        });
      },
    });

  cancelSubscription = () =>
    this.runAsync({
      key: 'cancellingSubscription',
      work: async () => {
        await this.state.profile?.cancelSubscription();
        this.setState({
          subscription: null,
        });
      },
    });

  verifyEmail = (emailToken: any) =>
    this.runAsync({
      key: 'verifyingEmail',
      work: async () => {
        const profile = await this.state.profile?.verifyEmail(emailToken);
        this.setState({ profile } as any);
      },
    });

  resendEmailVerification = () =>
    this.runAsync({
      key: 'isSendingEmailVerification',
      work: () => this.state.profile?.resendEmailVerification(),
    });

  toggleVerifyEmailBanner = async () => {
    this.setState({ hideEmailBanner: !this.state.hideEmailBanner });
  };

  consentedToHma = () =>
    !!this.state.profile?.legalDocuments?.find((document) => document.code === LegalCodes.HIPAAMarketingAuthorization);
}
